import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, AlertCircle, User, GraduationCap, Shield, Users } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';

const validationSchema = Yup.object({
  identifier: Yup.string()
    .required('Email or phone is required')
    .test('email-or-phone', 'Enter a valid email or phone number', function (value) {
      if (!value) return false;
      const emailValid = /@/.test(value) && Yup.string().email().isValidSync(value);
      const compact = value.replace(/\s|-/g, '');
      const phoneValid = /^(\+?\d{7,15}|0\d{10}|234\d{10})$/.test(compact);
      return emailValid || phoneValid;
    }),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

// Demo accounts for quick login
const demoAccounts = [
  {
    id: 'admin',
    label: 'Admin Demo',
    email: 'admin@passam.com',
    password: 'admin123',
    icon: Shield,
    description: 'Full system access',
    color: 'bg-red-500 hover:bg-red-600'
  },
  {
    id: 'teacher',
    label: 'Teacher Demo',
    email: 'teacher@passam.com',
    password: 'teacher123',
    icon: GraduationCap,
    description: 'Educator dashboard',
    color: 'bg-blue-500 hover:bg-blue-600'
  },
  {
    id: 'student',
    label: 'Student Demo',
    email: 'student@passam.com',
    password: 'student123',
    icon: User,
    description: 'Learning portal',
    color: 'bg-green-500 hover:bg-green-600'
  }
  ,
  {
    id: 'parent',
    label: 'Parent Demo',
    email: 'parent@passam.com',
    password: 'Parent123',
    icon: Users,
    description: 'Parent dashboard',
    color: 'bg-purple-500 hover:bg-purple-600'
  }
];

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const { appName } = useAppConfig();

  const from = location.state?.from?.pathname || '/dashboard';

  const formik = useFormik({
    initialValues: {
      identifier: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError('');
      
      try {
        const result = await login(values.identifier, values.password);
        if (result.success) {
          navigate(from, { replace: true });
        } else {
          setError(result.error || 'Login failed. Please try again.');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleQuickLogin = async (account) => {
    setIsLoading(true);
    setError('');
    
    try {
      const result = await login(account.email, account.password);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        const errMsg = result.error || 'Quick login failed. Please try again.';
        // Fallback: auto-register demo accounts that don't exist (parent/student only)
        const canAutoRegister = account.id === 'parent' || account.id === 'student';
        const isInvalidCreds = /invalid credentials/i.test(errMsg);
        if (canAutoRegister && isInvalidCreds) {
          try {
            const firstName = account.id === 'parent' ? 'Parent' : 'Student';
            const lastName = 'Demo';
            const role = account.id;
            const regResult = await register({
              firstName,
              lastName,
              email: account.email,
              password: account.password,
              role
            });
            if (regResult.success) {
              navigate(from, { replace: true });
              return;
            }
            setError(regResult.error || errMsg);
          } catch (regErr) {
            setError(regErr.response?.data?.message || errMsg);
          }
        } else {
          setError(errMsg);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Quick login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-exam rounded-xl mb-4">
            <span className="text-white font-semibold text-xl">PA</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            {appName}
          </h1>
          <p className="text-slate-500 text-sm mt-1">Exam preparation platform</p>
        </div>

        <Card className="shadow-card-hover">
          <CardHeader className="pb-4">
            <h2 className="card-title text-center">Sign in</h2>
          </CardHeader>
          <CardContent className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700 text-sm">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">Quick demo</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {demoAccounts.map((account) => {
                  const IconComponent = account.icon;
                  return (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => handleQuickLogin(account)}
                      disabled={isLoading}
                      className="flex flex-col items-center gap-1 p-3 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      <IconComponent className="h-5 w-5" />
                      <span className="text-xs">{account.label.replace(' Demo', '')}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-slate-500">Or sign in with email</span>
              </div>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <Input
                type="text"
                placeholder="Email or phone"
                icon={Mail}
                hasLeftIcon={true}
                {...formik.getFieldProps('identifier')}
                error={formik.touched.identifier && formik.errors.identifier}
              />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                icon={Lock}
                hasLeftIcon={true}
                hasRightIcon={true}
                rightIcon={
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-600 p-1 rounded"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                }
                {...formik.getFieldProps('password')}
                error={formik.touched.password && formik.errors.password}
              />
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer text-slate-600">
                  <input type="checkbox" className="rounded border-slate-300 text-exam focus:ring-exam" />
                  Remember me
                </label>
                <Link to="/forgot-password" className="text-primary-700 hover:text-exam font-medium">
                  Forgot password?
                </Link>
              </div>
              <Button
                type="submit"
                className="w-full"
                loading={isLoading}
                disabled={!formik.isValid || isLoading}
              >
                Sign in
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 pt-2 border-t border-slate-100">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-exam hover:underline">
                Create account
              </Link>
            </p>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-slate-400">© {appName}. For exam preparation.</p>
      </div>
    </div>
  );
};

export default Login;